// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import type { DitherParams, DitherResult, WorkerOut } from './types';
import { ALGO_LABELS } from './presets';

const ABORT = Symbol('superseded');
export function isAbort(err: unknown): boolean {
  return err === ABORT;
}

export interface Progress {
  phase: 'decode' | 'dither' | 'encode';
  pct: number;
}

/**
 * Owns the dithering worker. Each `dither()` supersedes any in-flight job, so a
 * fast run of slider changes only ever resolves the newest.
 */
export class Ditherer {
  private worker: Worker | null = null;
  private jobId = 0;
  private pending: {
    jobId: number;
    resolve: (r: DitherResult) => void;
    reject: (e: unknown) => void;
    onProgress?: (p: Progress) => void;
    startedAt: number;
    params: DitherParams;
  } | null = null;

  private ensureWorker(): Worker {
    if (this.worker) return this.worker;
    const w = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (e: MessageEvent<WorkerOut>) => this.onMessage(e.data);
    w.onerror = () => {
      if (this.pending) {
        this.pending.reject(new Error('The dithering worker crashed.'));
        this.pending = null;
      }
    };
    this.worker = w;
    return w;
  }

  dither(
    blob: Blob,
    params: DitherParams,
    onProgress?: (p: Progress) => void,
  ): Promise<DitherResult> {
    const worker = this.ensureWorker();
    // Supersede any in-flight job.
    if (this.pending) this.pending.reject(ABORT);
    const jobId = ++this.jobId;
    return new Promise<DitherResult>((resolve, reject) => {
      this.pending = { jobId, resolve, reject, onProgress, startedAt: Date.now(), params };
      worker.postMessage({ type: 'dither', jobId, blob, params });
    });
  }

  private onMessage(msg: WorkerOut): void {
    const p = this.pending;
    if (!p || msg.jobId !== p.jobId) return; // stale
    if (msg.type === 'progress') {
      p.onProgress?.({ phase: msg.phase, pct: msg.pct });
      return;
    }
    if (msg.type === 'error') {
      this.pending = null;
      p.reject(new Error(msg.message));
      return;
    }
    // done
    this.pending = null;
    p.resolve({
      blob: msg.blob,
      bytes: msg.blob.size,
      width: msg.width,
      height: msg.height,
      artWidth: msg.artWidth,
      artHeight: msg.artHeight,
      colors: msg.colors,
      algorithm: ALGO_LABELS[p.params.algorithm],
      ms: Date.now() - p.startedAt,
    });
  }

  cancel(): void {
    if (this.pending) {
      this.pending.reject(ABORT);
      this.pending = null;
    }
  }

  dispose(): void {
    this.cancel();
    this.worker?.terminate();
    this.worker = null;
  }
}
